import { Scale, FileText, AlertCircle, CheckCircle, Phone, ArrowRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import QuickContact from "@/components/QuickContact";
import { Button } from "@/components/ui/button";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLanguage } from "@/hooks/useLanguage";
import { RelatedLinks } from "@/components/RelatedLinks";
import AIReadableSection from "@/components/AIReadableSection";
import { taxLawFAQs } from "@/lib/practice-area-faqs";
import HeroSection from "@/components/HeroSection";
import taxHeroImage from "@/assets/tax-law-hero.jpg";

const TaxLaw = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      hero: { title: "Tax Law Services", subtitle: "Comprehensive Tax Resolution, Planning & Litigation", schedule: "Schedule Consultation", call: "Call Now" },
      
      services: { title: "Our Tax Law Services", subtitle: "Comprehensive tax solutions for individuals and businesses facing IRS issues, audits, and tax planning needs." },
      items: [
        { title: "Tax Litigation", description: "Aggressive representation in tax disputes and audits before IRS and state agencies." },
        { title: "Tax Planning", description: "Strategic tax planning to minimize liability and maximize savings." },
        { title: "Tax Debt Resolution", description: "Negotiate and resolve outstanding tax debts with federal and state authorities." }
      ],
      why: { title: "Why Choose Daniel Albert Law for Tax Issues?" },
      whyItems: [
        { title: "IRS Experience", description: "Proven track record resolving complex IRS disputes and negotiations" },
        { title: "Strategic Planning", description: "Proactive tax planning to minimize future liabilities" },
        { title: "Responsive Service", description: "Direct access to your attorney throughout the process" }
      ]
    },
    es: {
      hero: { title: "Servicios de Derecho Fiscal", subtitle: "Resolución Fiscal Experta, Planificación y Litigios", schedule: "Agendar Consulta", call: "Llamar Ahora" },
      
      services: { title: "Nuestros Servicios de Derecho Fiscal", subtitle: "Soluciones fiscales integrales para individuos y empresas que enfrentan problemas del IRS, auditorías y necesidades de planificación fiscal." },
      items: [
        { title: "Litigio Fiscal", description: "Representación agresiva en disputas fiscales y auditorías ante el IRS y agencias estatales." },
        { title: "Planificación Fiscal", description: "Planificación fiscal estratégica para minimizar responsabilidad y maximizar ahorros." },
        { title: "Resolución de Deudas Fiscales", description: "Negociar y resolver deudas fiscales pendientes con autoridades federales y estatales." }
      ],
      why: { title: "¿Por Qué Elegir Daniel Albert Law para Asuntos Fiscales?" },
      whyItems: [
        { title: "Experiencia con IRS", description: "Historial comprobado resolviendo disputas y negociaciones complejas del IRS" },
        { title: "Planificación Estratégica", description: "Planificación fiscal proactiva para minimizar responsabilidades futuras" },
        { title: "Servicio Receptivo", description: "Acceso directo a su abogado durante todo el proceso" }
      ]
    },
    vi: {
      hero: { title: "Dịch Vụ Luật Thuế", subtitle: "Giải Quyết Thuế Chuyên Nghiệp, Lập Kế Hoạch & Tranh Tụng", schedule: "Đặt Lịch Tư Vấn", call: "Gọi Ngay" },
      
      services: { title: "Dịch Vụ Luật Thuế Của Chúng Tôi", subtitle: "Giải pháp thuế toàn diện cho cá nhân và doanh nghiệp đối mặt với vấn đề IRS, kiểm toán và nhu cầu lập kế hoạch thuế." },
      items: [
        { title: "Tranh Tụng Thuế", description: "Đại diện quyết liệt trong tranh chấp thuế và kiểm toán trước IRS và các cơ quan tiểu bang." },
        { title: "Lập Kế Hoạch Thuế", description: "Lập kế hoạch thuế chiến lược để giảm thiểu trách nhiệm và tối đa hóa tiết kiệm." },
        { title: "Giải Quyết Nợ Thuế", description: "Đàm phán và giải quyết nợ thuế tồn đọng với chính quyền liên bang và tiểu bang." }
      ],
      why: { title: "Tại Sao Chọn Daniel Albert Law Cho Vấn Đề Thuế?" },
      whyItems: [
        { title: "Kinh Nghiệm IRS", description: "Thành tích đã được chứng minh giải quyết tranh chấp và đàm phán IRS phức tạp" },
        { title: "Lập Kế Hoạch Chiến Lược", description: "Lập kế hoạch thuế chủ động để giảm thiểu trách nhiệm trong tương lai" },
        { title: "Dịch Vụ Phản Hồi Nhanh", description: "Truy cập trực tiếp luật sư của bạn trong suốt quá trình" }
      ]
    }
  };

  const pageContent = content[language];
  const serviceRoutes = [
    "/practice-areas/tax-law/tax-litigation",
    "/practice-areas/tax-law/tax-planning",
    "/practice-areas/tax-law/tax-debt-resolution"
  ];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section - Optimized for CWV */}
      <HeroSection
        imageSrc={taxHeroImage}
        imageAlt="Tax Law Attorney Houston - IRS Resolution, Tax Planning"
        height="60vh"
        overlayGradient="bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95"
        priority={true}
      >
        <div className="text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">{pageContent.hero.title}</h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">{pageContent.hero.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="cta">
              <LocalizedLink to="/appointment">{pageContent.hero.schedule}</LocalizedLink>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/20">
              <a href="tel:8329303059"><Phone className="mr-2 h-5 w-5" />{pageContent.hero.call}: (832) 930-3059</a>
            </Button>
          </div>
        </div>
      </HeroSection>


      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">{pageContent.services.title}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{pageContent.services.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pageContent.items.map((service, index) => (
              <Card key={index} className="hover:shadow-xl transition-all hover-scale duration-300 bg-card/80 backdrop-blur-sm border-accent/20">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-gradient-gold-shine flex items-center justify-center mb-4 shadow-gold">
                    {index === 0 ? <Scale className="h-6 w-6 text-primary" /> : index === 1 ? <FileText className="h-6 w-6 text-primary" /> : <AlertCircle className="h-6 w-6 text-primary" />}
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription>{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="link" className="p-0 text-accent hover:text-accent/80">
                    <LocalizedLink to={serviceRoutes[index]}>Más Información <ArrowRight className="ml-1 h-4 w-4" /></LocalizedLink>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <QuickContact />

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">{pageContent.why.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pageContent.whyItems.map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-accent mb-4 flex justify-center">
                  {index === 0 ? <Scale className="h-12 w-12" /> : index === 1 ? <FileText className="h-12 w-12" /> : <Phone className="h-12 w-12" />}
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AIReadableSection 
        title={taxLawFAQs[language].title}
        summary={taxLawFAQs[language].summary}
      />

      <RelatedLinks category="tax" />

      <CTASection />
      <Footer />
    </div>
  );
};

export default TaxLaw;
